import React, {useMemo, useContext, useEffect, useState, ReactNode, useRef} from 'react';
import { Member, listAudiences, loadAudience, updateAudience } from "../audience-api";
import { useHeaders } from "../audience/AudiencePage";
import { Column, Table } from "../table/Table";
import { CampaignContext, MStatus } from './campaign-context';
import { MemberNavigation, PreviewMessage, createMemberNavigation } from '../audience/message-preview/MessagePreview';
import { Modal } from '../components/Modal';
import { SavingButton } from '../components/SavingButton';
import { Campaign, checkCampaignInteraction, messageStatus as loadMessageStatus, updateCampaign } from '../campaign-api';
import { Loading } from '../components/Loading';
import { MediaItem, loadMedia } from '../media/media-api';
import { MediaPreviewBox } from '../media/media-preview';
import { VariablePicker } from '../audience/message-preview/VariablePicker';
import { GPTWizard } from '../audience/message-preview/gpt-wizard';
import { ExtendedTemplateStore, useSelectedTemplate } from './extended-template-store';
import { ExtendedTemplateEditor, ExtendedTemplateMenu } from './extented-templates';
import { MessageBox } from '../comps/MessageBox';
import { MediaMenu } from '../media/media-selector';
import { SendMessageModalButton } from './send-message-modal';
import { useConfirm } from '../components/Dialogs';

function SentStatus({member}:{member:Member}){
    const {state:{messageStatus}} = useContext(CampaignContext);
    const status = messageStatus[member.id];

    if(status == null) return <></>
    if(status.hasInteraction) return <i className='fa fa-fire text-warning'></i>
    if(status.isRead) return <i className='fa fa-check-double text-primary'></i>
    if(status.isDelivered) return <i className='fa fa-check'></i>
        
    return <></>
}

function useColumns(rows:Member[], messageStatus:MStatus):Column<Member>[] {
    const headers = useHeaders(rows);
    return useMemo(() => {
        if(headers == null) return null;
        let b:Column<Member>[] = [];

        if(messageStatus){
            b = [{id:"a", thGroup:"th-check", colGroup:"td-view-status", label:<></>, render: r => <SentStatus member={r} />}]
        }

        return [...b, ... headers.map(h => ({id:h, label:h, render: (r) => <>{r.sourceMap[h]}</>}) as Column<Member>)]

    }, [headers, messageStatus]);
}

export function MemberListPreview(){
    const {state:{viewCampaign, members, bot, messageStatus}, dispatch} = useContext(CampaignContext);
    const [nav, setNav] = useState<MemberNavigation>(null)

    useEffect(()=>{
        if(!viewCampaign?.audience) return;
        (async function (){
            const audience = await loadAudience(viewCampaign.audience)
            const members = audience.members.filter(m => m.isWAPhone !== false);
            dispatch({type:"setMembers", members})
        })()
    }, [viewCampaign?.audience])

    useEffect(() => {
        
        if(!['done', 'paused'].includes(viewCampaign?.status)) {
            dispatch({type:"setMessageStatus", mStatus: {}})
            return ;
        }

        (async function (){
            const res = await loadMessageStatus(bot, viewCampaign)
            const mStatus:MStatus = {};
            res.forEach(r => mStatus[r.member] = r)

            dispatch({type:"setMessageStatus", mStatus})
        })()
    }, [viewCampaign?.status])

    const columns = useColumns(members, messageStatus)
    const variables = useHeaders(members);

    if(!members) {
        return <Loading className='mb-3' />
    }

    if(members.length == 0) {
        return <div>Não há nenhum contato ativo no whats do público <b>{viewCampaign.audienceName}</b></div>
    }

    function showMessagePreview(m:Member){
        let index = members.findIndex(i => i == m)
        setNav(createMemberNavigation(members, index))
    }

    return <>
        {viewCampaign.status == 'none' && <MessageBox className='mb-3'>
                Antes de enviar as mensagens, clique em uma linha da tabela de contatos e veja um preview da mensagem e faça os ajustes finais.
            </MessageBox>}
        
        {['none', 'paused'].includes(viewCampaign.status) && <button className='btn btn-secondary me-2' onClick={() => setNav(createMemberNavigation(members, 0)) }>Ajustar Mensagem</button>}
        <SendMessageModalButton />
        {viewCampaign?.status == 'done' && <CheckInteractionButton />}

        <div className="mb-2 mt-4"><b>{members.length}</b> contatos de Whatsapp em <b>{viewCampaign.audienceName}</b></div>
        <div className="table-view mb-3" style={{maxHeight: "calc(100vh - 412px)", position: "relative", overflow:"auto"}}>
            <Table columns={columns} data={members} onClick={showMessagePreview} />
        </div>
        
        {nav && <MessagePreview onHide={() => setNav(null)} navigation={nav} variables={variables} />}
    </>
    
}

function CheckInteractionButton(){
    const {state:{viewCampaign, bot}} = useContext(CampaignContext)
    const confirm = useConfirm("Esta tarefa irá demorar alguns instantes. Deseja iniciar agora", {})

    async function click(){
        const ok = await confirm()
        if(ok){
            await checkCampaignInteraction(bot, viewCampaign)
        }
    }

    return <button className='ms-2 btn btn-info' onClick={click}>Checar Interações</button>
}


function MessagePreviewModal({navigation, variables}:{navigation:MemberNavigation, variables: string[]}){
    const {state:{viewCampaign, bot}, dispatch} = useContext(CampaignContext);
    const [message, setMessage] = useState(viewCampaign.template)
    const [saving, setSaving] = useState(false);
    const [nav, setNav] = useState(navigation)
    const [media, setMedia] = useState<MediaItem>(null);
    const textRef = useRef<HTMLTextAreaElement>(null);
    const [gptMode, setGptMode] = useState(false);
    const selectedTemplate = useSelectedTemplate();

    async function updateTemplate(){
        let campaign:Campaign = {...viewCampaign, template: message}
        setSaving(true)
        try {
            dispatch({type:"setViewCampaign", campaign});
            await updateCampaign(bot, campaign);
        }catch(e){
            console.error(e);
        }
        setSaving(false);
    }

    async function updateMedia(e: number){
        let campaign:Campaign = {...viewCampaign, media: e}
        setSaving(true)
        dispatch({type:"setViewCampaign", campaign});
        try {
            await updateCampaign(bot, campaign);
        }catch(e){
            console.error(e);
        }
        setSaving(false);
    }


    useEffect(()=>{
        (async function(){
            setMedia(await loadMedia(viewCampaign?.media));
        })();
    }, [viewCampaign?.media])

    if(selectedTemplate)
        return <ExtendedTemplateEditor template={selectedTemplate} variables={variables} navigation={navigation} />

    if(gptMode)
        return <GPTWizard template={message} variables={variables} onCancel={() => setGptMode(false)} onSuggestion={(s) => {setGptMode(false); setMessage(s)}} />

    if(media?.mimetype.startsWith("audio")){
        return <>
            <ExtendedTemplateMenu />
            <div className='mt-3 mb-1'>Mídia:</div>
            <MediaMenu value={viewCampaign.media} onChange={(e) => updateMedia(e)} /> 

            <hr />
            
            <div className='members-nav text-center'>
                {nav.current} / {nav.total}  
                <button className='btn btn-primary ms-2' onClick={() => setNav(nav.prev())} disabled={!nav.hasPrev}><i className='fa fa-arrow-left'></i></button>
                <button className='btn btn-primary ms-2' onClick={() => setNav(nav.next())} disabled={!nav.hasNext}><i className='fa fa-arrow-right'></i></button>
            </div>
        </>
    }

    return <>
        <ExtendedTemplateMenu />
        <VariablePicker variables={variables} setText={setMessage} textRef={textRef} />
        <textarea ref={textRef} rows={5} className='form-control mt-3' placeholder="Digite seu texto aqui..." value={message} onChange={(e) => setMessage(e.target.value)} />
        {/*media != null && <div className='mt-3'><MediaPreviewBox media={media} short={true} /></div>*/}

        <div className='mt-3 mb-1'>Mídia:</div>
        <MediaMenu value={viewCampaign.media} onChange={(e) => updateMedia(e)} /> 

        <PreviewMessage template={message} member={nav.member} onGPT={() => {setGptMode(true)}} />
        {message != viewCampaign.template && <SavingButton onClick={updateTemplate} saving={saving} icon='fa-save' className='btn btn-primary mt-3'>Salvar Template</SavingButton>}
        <hr />
        <div className='members-nav text-center'>
            {nav.current} / {nav.total}  
            <button className='btn btn-primary ms-2' onClick={() => setNav(nav.prev())} disabled={!nav.hasPrev}><i className='fa fa-arrow-left'></i></button>
            <button className='btn btn-primary ms-2' onClick={() => setNav(nav.next())} disabled={!nav.hasNext}><i className='fa fa-arrow-right'></i></button>
        </div>
    </>
}

export function MessagePreview({onHide, navigation, variables}:{onHide:()=>void, navigation:MemberNavigation, variables: string[]}){
    return <Modal title="Preview de Mensagem" onHide={onHide} className='modal-primary'>
        <ExtendedTemplateStore initialValue={{selected: null, templates: null}} >
            <MessagePreviewModal navigation={navigation} variables={variables} />
        </ExtendedTemplateStore>
    </Modal>
}